<template>
  <div>
    <button v-if="!showChat" class="border border-primary p-2 rounded-lg hover:bg-primary text-primary hover:text-white"
      @click="toggleChat">
      <MessagesSquare class="w-8 h-8" />
      <span v-if="unreadMessagesCount > 0"
        class="absolute text-xs text-accent-primary border-accent-primary border bg-white rounded-full px-1 py-0.5">
        {{ unreadMessagesCount }}
      </span>
    </button>
    <div v-if="showChat">
      <!-- chat window -->
      <div class="flex flex-row bg-primary text-white p-2 text-sm text-center rounded-t-md items-center">
        <div class="flex-1 flex justify-center">
          <span v-if="!props.isClinician">
            Chat With Your Therapist
          </span>
          <span v-if="props.isClinician">
            Chat With {{ otherUser?.nickname }} ({{ otherUser?.full_name }})
          </span>
        </div>
        <button v-if="!props.isClinician" class="ml-auto mr-2" @click="toggleChat">
          <SquareX />
        </button>
      </div>
      <div
        class="flex flex-col w-full rounded-b-md min-h-40 bg-slate-100 justify-end overflow-hidden overflow-y-auto border-slate-300 border-0.5 drop-shadow-md mb-4">
        <!-- conversation -->
        <div class="conversation flex flex-col w-full min-h-8 h-full overflow-y-auto ">
          <span v-if="visible">
            <ChatBubbleComponent v-for="message in messages" :key="message.id" :id="message.id" :msg="message.text"
              :reply="message.author_id != user?.id" />
          </span>

          <span v-if="!visible">
            <ChatBubbleComponent
              :msg="'Hi ' + user.nickname + '! Welcome to Hope Kit! This chat will fully unlock once you have completed the intro module “Getting Started at Hope Kit”.'"
              :reply="true" :id="0" />
          </span>
        </div>
        <!-- input box -->
        <div class="flex w-full min-h-8 content-end flex-row" v-if="props.visible">
          <textarea ref="messageInput" id="messageInput"
            class="w-full h-full border-slate-400 border-0.5 rounded-md mx-2 px-1 overflow-y-hidden" rows="1"
            v-model="newMessage" @keyup.enter="addMessage" />
          <div name="messageSubmit" class="w-7 h-full content-end mr-2" v-on:click="addMessage">
            <Send class="text-primary fill-secondary"></Send>
          </div>
        </div>
        <div class="flex w-full min-h-8 content-end flex-row" v-if="!props.visible">
          <textarea ref="messageInput" id="messageInput"
            class="w-full h-full border-slate-400 border-0.5  bg-gray-300 rounded-md mx-2 px-1 overflow-y-hidden"
            disabled="true" rows="1" @click="unlockLaterMessage"
            placeholder="Chat will unlock after the first module" />
          <div name="messageSubmit" class="w-7 h-7 content-start mr-2 mt-0.5" v-on:click="unlockLaterMessage">
            <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 28 28">
              <g fill="none" stroke="#5D7EB2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 14L21 3"></path>
                <path d="M21 3l-6.5 18a.55.55 0 0 1-1 0L10 14l-7-3.5a.55.55 0 0 1 0-1L21 3"></path>
              </g>
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script lang="ts" setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount, onUnmounted } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { MessagesSquare } from 'lucide-vue-next';
import ChatBubbleComponent from './ChatBubbleComponent.vue'
import { useMessagesStore, type Message, type MessagesStream } from '@/stores/messages'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { type User } from '@/stores/user'
import { isErrorModel } from '@/models/error'
import { Send } from 'lucide-vue-next'
import { SquareX } from 'lucide-vue-next'

const scrollToBottom = async () => {
  await nextTick()
  const chatWindow = document.querySelector('.conversation')
  if (chatWindow !== null) chatWindow.scrollTo(0, chatWindow.scrollHeight + 1000)
}

const props = defineProps<{
  isClinician: boolean | undefined,
  recipientId: number | undefined,
  visible: boolean
}>()

const newMessage = ref('')
const messagesStore = useMessagesStore()
const authStore = useAuthStore()
const userStore = useUserStore()
const messages = ref<Message[]>([])
const user = userStore.current_user
let otherUser = ref<User | null>(null)
const showChat = ref(localStorage.getItem('showChat') !== 'false')
const unreadMessagesCount = ref(0)

let stream = ref<MessagesStream | null>(null)

const toggleChat = () => {
  showChat.value = !showChat.value
  if (showChat.value) {
    scrollToBottom()
  }
  localStorage.setItem('showChat', showChat.value.toString())
}

const addMessage = async () => {
  if (newMessage.value === '') return
  const msgText = newMessage.value
  newMessage.value = ''
  let message: Message | null = null
  unreadMessagesCount.value = 0
  try {
    if (props.isClinician) {
      message = await messagesStore.clinicianAddMessage(msgText, props.recipientId!)
    } else {
      message = await messagesStore.addMessage(msgText)
    }
    if (!messages.value.find((m: Message) => m.id === message!.id)) {
      messages.value.push(message)
    }

    const messageInput = document.getElementById('messageInput')
    if (messageInput) {
      messageInput.style.height = 'auto'
      messageInput.style.height = `${messageInput.scrollHeight}px`
    }
  } catch (e) {
    newMessage.value = msgText
    throw e
  }
  scrollToBottom()
  // Mark all old messages as read, particularly if we missed it.
  const isUser = await userStore.IsUser()
  const markReadFn = isUser ? messagesStore.markClinicianMessagesRead : messagesStore.markClientMessagesRead
  for (let i = 0; i < messages.value.length; i++) {
    if (!messages.value[i].read && messages.value[i].author_id !== user?.id) {
      markReadFn(messages.value[i].author_id, messages.value[i].id)
      messages.value[i].read = true
    }
  }
}

const unlockLaterMessage = async () => {
  alert('Chat will unlock after the first module is completed.')
}

onMounted(async () => {
  if (!authStore.isAuthenticated) {
    return
  }
  const isUser = await userStore.IsUser()
  if (isUser) {
    await messagesStore.getMessages()
    if (messages.value.length > 0) {
      const latestMessage = messages.value[messages.value.length - 1]
      if (latestMessage.author_id == latestMessage.clinician_id && !latestMessage.read) {
        try {
          await messagesStore.markClinicianMessagesRead(latestMessage.author_id, latestMessage.id)
          await messagesStore.getMessages()
        } catch (error) {
          console.error('Failed to mark messages as read:', error)
        }
      }
    }
  }

  const isClinician = await userStore.IsClinician()
  if (isClinician && props.recipientId !== undefined) {
    await messagesStore.getClinicianMessagesForUser(props.recipientId!)
    if (messages.value.length > 0) {
      const latestClientMessages = messages.value.filter((msg) => msg.author_id == msg.client_id)
      const latestClientMessage = latestClientMessages[latestClientMessages.length - 1]
      if (latestClientMessage.author_id == latestClientMessage.client_id && !latestClientMessage.read) {
        try {
          await messagesStore.markClientMessagesRead(latestClientMessage.author_id, latestClientMessage.id)
          await messagesStore.getClinicianMessagesForUser(props.recipientId!)
        } catch (error) {
          console.error('Failed to mark messages as read:', error)
        }
      }
    }
  }
})

onBeforeRouteLeave((to, from, next) => {
  if (stream.value !== null) {
    stream.value.close()
  }
  next()
})

onBeforeUnmount(() => {
  if (stream.value !== null) {
    stream.value.close()
  }
})

onUnmounted(() => {
  if (stream.value !== null) {
    stream.value.close()
  }
})

watch(newMessage, () => {
  const messageInput = document.getElementById('messageInput')
  if (messageInput) {
    messageInput.style.height = 'auto'
    messageInput.style.height = `${messageInput.scrollHeight}px`
  }
})

watch(
  () => props.recipientId,
  async () => {
    if (!authStore.isAuthenticated) {
      return
    }
    const isClinician = await userStore.IsClinician()
    const isUser = await userStore.IsUser()

    if (stream.value !== null) {
      stream.value.close()
    }
    if (isClinician && props.recipientId !== undefined) {
      messages.value = []
      let resp = await messagesStore.getClinicianMessagesPollingPrivate(props.recipientId!, (m: Message) => {
        if (!messages.value.find((msg: Message) => msg.id === m.id)) {
          messages.value.push(m)
          scrollToBottom()
        }
      })
      stream.value = resp

      if (otherUser.value === null || otherUser.value.id !== props.recipientId) {
        let resp2 = await userStore.clinicianGetUser(props.recipientId!)
        if (!isErrorModel(resp2)) {
          otherUser.value = resp2
        }
      }
    } else if (isUser && props.recipientId === undefined) {
      messages.value = []
      stream.value = await messagesStore.getMessagesPolling(async (m: Message) => {
        if (!messages.value.find((msg: Message) => msg.id === m.id)) {
          messages.value.push(m)
          if (showChat.value) {
            scrollToBottom()
          } else {
            // increment unread messages
            if (!m.read && m.author_id !== user?.id) {
              unreadMessagesCount.value++
            }
          }
        }
      })
    }
  },
  { immediate: true }
)

watch(
  messages,
  async (newMessages) => {
    const isUser = await userStore.IsUser()
    if (isUser) {
      if (newMessages.length > 0) {
        const latestMessage = newMessages[newMessages.length - 1]
        if (latestMessage.author_id == latestMessage.clinician_id && !latestMessage.read) {
          try {
            await messagesStore.markClinicianMessagesRead(latestMessage.author_id, latestMessage.id)
            await messagesStore.getMessages()
          } catch (error) {
            console.error('Failed to mark messages as read:', error)
          }
        }
      }
    }

    const isClinician = await userStore.IsClinician()
    if (isClinician && props.recipientId !== undefined) {
      await messagesStore.getClinicianMessagesForUser(props.recipientId!)
      if (messages.value.length > 0) {
        const latestClientMessages = messages.value.filter((msg) => msg.author_id == msg.client_id)
        const latestClientMessage = latestClientMessages[latestClientMessages.length - 1]
        if (latestClientMessage.author_id == latestClientMessage.client_id && !latestClientMessage.read) {
          try {
            await messagesStore.markClientMessagesRead(latestClientMessage.author_id, latestClientMessage.id)
            await messagesStore.getClinicianMessagesForUser(props.recipientId!)
          } catch (error) {
            console.error('Failed to mark messages as read:', error)
          }
        }
      }
    }
  },
  { deep: true }
)

</script>
